/**
 * Chat Rate Limiter
 * Application-level rate limiting for chat messages
 * 
 * Limits:
 * - 20 messages per minute per room
 * - 100 messages per hour per user
 */

interface RateLimitEntry {
  count: number;
  resetAt: number;
}

class ChatRateLimiter {
  private roomLimits: Map<string, RateLimitEntry> = new Map();
  private userLimits: Map<string, RateLimitEntry> = new Map();

  // Configuration
  private readonly ROOM_LIMIT_PER_MINUTE = 20;
  private readonly USER_LIMIT_PER_HOUR = 100;
  private readonly MINUTE_MS = 60 * 1000;
  private readonly HOUR_MS = 60 * 60 * 1000;

  /**
   * Check if sending a message is allowed
   * @returns { allowed: boolean, retryAfterMs?: number, reason?: string }
   */
  checkLimit(
    userId: string,
    roomId: string
  ): { allowed: boolean; retryAfterMs?: number; reason?: string } {
    const now = Date.now();

    // Check room limit (20/min)
    const roomKey = `room:${roomId}`;
    const roomEntry = this.roomLimits.get(roomKey);

    if (roomEntry) {
      if (now < roomEntry.resetAt) {
        if (roomEntry.count >= this.ROOM_LIMIT_PER_MINUTE) {
          return {
            allowed: false,
            retryAfterMs: roomEntry.resetAt - now,
            reason: 'Слишком много сообщений в этом чате. Подождите минуту.',
          };
        }
      } else {
        // Reset expired entry
        this.roomLimits.delete(roomKey);
      }
    }

    // Check user limit (100/hr)
    const userKey = `user:${userId}`;
    const userEntry = this.userLimits.get(userKey);

    if (userEntry) {
      if (now < userEntry.resetAt) {
        if (userEntry.count >= this.USER_LIMIT_PER_HOUR) {
          return {
            allowed: false,
            retryAfterMs: userEntry.resetAt - now,
            reason: 'Вы отправили слишком много сообщений. Подождите немного.',
          };
        }
      } else {
        // Reset expired entry
        this.userLimits.delete(userKey);
      }
    }

    return { allowed: true };
  }

  /**
   * Record a sent message
   */
  recordMessage(userId: string, roomId: string): void {
    const now = Date.now();

    // Update room counter
    const roomKey = `room:${roomId}`;
    const roomEntry = this.roomLimits.get(roomKey);
    
    if (roomEntry && now < roomEntry.resetAt) {
      roomEntry.count += 1;
    } else {
      this.roomLimits.set(roomKey, {
        count: 1,
        resetAt: now + this.MINUTE_MS,
      });
    }

    // Update user counter
    const userKey = `user:${userId}`;
    const userEntry = this.userLimits.get(userKey);
    
    if (userEntry && now < userEntry.resetAt) {
      userEntry.count += 1;
    } else {
      this.userLimits.set(userKey, {
        count: 1,
        resetAt: now + this.HOUR_MS,
      });
    }

    // Periodic cleanup of old entries
    if (Math.random() < 0.01) {
      this.cleanup();
    }
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();

    for (const [key, entry] of this.roomLimits) {
      if (now >= entry.resetAt) {
        this.roomLimits.delete(key);
      }
    }

    for (const [key, entry] of this.userLimits) {
      if (now >= entry.resetAt) {
        this.userLimits.delete(key);
      }
    }
  }

  /**
   * Get current limits info (for debugging/monitoring)
   */
  getStats(): { roomEntries: number; userEntries: number } {
    return {
      roomEntries: this.roomLimits.size,
      userEntries: this.userLimits.size,
    };
  }
}

// Singleton instance
export const chatRateLimiter = new ChatRateLimiter();
